import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  FileDown,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  useCreateOrderMutation,
  useCreateBulkOrdersMutation,
  useImportOrdersCsvMutation,
} from "@/store/ordersApi";
import { useGetProductsQuery } from "@/store/productsApi";

// Helper to format currency
const formatPrice = (price: number) => `₦${price.toLocaleString()}`;

export function AdminCreateOrderScreen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("single");

  // API mutations
  const [createOrder, { isLoading: isCreating }] = useCreateOrderMutation();
  const [createBulkOrders, { isLoading: isBulkCreating }] =
    useCreateBulkOrdersMutation();
  const [importCsv, { isLoading: isImporting }] = useImportOrdersCsvMutation();

  // Load products for dropdown
  const { data: productsData } = useGetProductsQuery({ limit: 100 });
  const products = productsData?.data || [];

  // --- Single Order State ---
  const [singleOrder, setSingleOrder] = useState({
    userInfo: { email: "", name: "", phone: "" },
    items: [{ productId: "", quantity: 1 }],
    deliveryAddress: {
      label: "Home",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "Nigeria",
    },
    paymentMethod: "CASH",
    notes: "",
  });

  // --- Bulk Order State ---
  // Array of order objects (simplified for UI)
  const [bulkOrders, setBulkOrders] = useState([
    {
      userInfo: { email: "", name: "", phone: "" },
      items: [{ productId: "", quantity: 1 }],
      deliveryAddress: {
        label: "Home",
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "Nigeria",
      },
      paymentMethod: "CASH",
    },
  ]);

  // --- CSV Import State ---
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // --- Handlers for Single Order ---
  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createOrder(singleOrder).unwrap();
      toast.success("Order Created", {
        description: "The order has been successfully created.",
      });
      navigate("/admin/orders");
    } catch (error: any) {
      toast.error("Error", {
        description: error.data?.message || "Failed to create order",
      });
    }
  };

  const addProductToSingle = () => {
    setSingleOrder({
      ...singleOrder,
      items: [...singleOrder.items, { productId: "", quantity: 1 }],
    });
  };

  const removeProductFromSingle = (index: number) => {
    const newItems = singleOrder.items.filter((_, i) => i !== index);
    setSingleOrder({ ...singleOrder, items: newItems });
  };

  // --- Handlers for Bulk Order ---
  const addBulkOrderRow = () => {
    setBulkOrders([
      ...bulkOrders,
      {
        userInfo: { email: "", name: "", phone: "" },
        items: [{ productId: "", quantity: 1 }],
        deliveryAddress: {
          label: "Home",
          street: "",
          city: "",
          state: "",
          zipCode: "",
          country: "Nigeria",
        },
        paymentMethod: "CASH",
      },
    ]);
  };

  const removeBulkOrderRow = (index: number) => {
    const newOrders = bulkOrders.filter((_, i) => i !== index);
    setBulkOrders(newOrders);
  };

  const handleBulkSubmit = async () => {
    try {
      const res = await createBulkOrders(bulkOrders).unwrap();
      toast.success("Bulk Process Complete", {
        description: res.message,
      });
      if (res.failed === 0) {
        navigate("/admin/orders");
      }
    } catch (error: any) {
      toast.error("Error", {
        description: error.data?.message || "Failed to process bulk orders",
      });
    }
  };

  // --- Handlers for CSV Import ---
  const handleCsvUpload = async () => {
    if (!csvFile) return;
    const formData = new FormData();
    formData.append("file", csvFile);

    try {
      const res = await importCsv(formData).unwrap();
      toast.success("CSV Import Complete", {
        description: res.message,
      });
      navigate("/admin/orders");
    } catch (error: any) {
      toast.error("Error", {
        description: error.data?.error || "Failed to import CSV",
      });
    }
  };

  const downloadCsvTemplate = () => {
    const headers =
      "email,name,phone,address_label,street,city,state,zip,items,paymentMethod,notes";
    const example =
      "user@example.com,John Doe,08012345678,Home,123 Main St,Lagos,Lagos,100001,productId1:2|productId2:1,CASH,Leave at gate";
    const csvContent =
      "data:text/csv;charset=utf-8," + headers + "\n" + example;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "order_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin/orders")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Order</h1>
          <p className="text-muted-foreground">
            Create single or bulk orders manually or via CSV
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-[400px]">
          <TabsTrigger value="single">Single Order</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Manual</TabsTrigger>
          <TabsTrigger value="import">Import CSV</TabsTrigger>
        </TabsList>

        {/* --- Single Order Tab --- */}
        <TabsContent value="single">
          <Card>
            <CardHeader>
              <CardTitle>New Order Details</CardTitle>
              <CardDescription>
                Enter customer and order information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Email</Label>
                  <Input
                    value={singleOrder.userInfo.email}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        userInfo: {
                          ...singleOrder.userInfo,
                          email: e.target.value,
                        },
                      })
                    }
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input
                    value={singleOrder.userInfo.name}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        userInfo: {
                          ...singleOrder.userInfo,
                          name: e.target.value,
                        },
                      })
                    }
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone (Optional)</Label>
                  <Input
                    value={singleOrder.userInfo.phone}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        userInfo: {
                          ...singleOrder.userInfo,
                          phone: e.target.value,
                        },
                      })
                    }
                    placeholder="080..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select
                    value={singleOrder.paymentMethod}
                    onValueChange={(val) =>
                      setSingleOrder({ ...singleOrder, paymentMethod: val })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="CARD">Card</SelectItem>
                      <SelectItem value="ONLINE">Online</SelectItem>
                      <SelectItem value="WALLET">Wallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-base">Products</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addProductToSingle}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Item
                  </Button>
                </div>

                {singleOrder.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-end gap-3 p-3 border rounded-md"
                  >
                    <div className="flex-1 space-y-2">
                      <Label>Product</Label>
                      <Select
                        value={item.productId}
                        onValueChange={(val) => {
                          const newItems = [...singleOrder.items];
                          newItems[index].productId = val;
                          setSingleOrder({ ...singleOrder, items: newItems });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} - {formatPrice(Number(p.price))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24 space-y-2">
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...singleOrder.items];
                          newItems[index].quantity =
                            parseInt(e.target.value) || 1;
                          setSingleOrder({ ...singleOrder, items: newItems });
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive mb-0.5"
                      onClick={() => removeProductFromSingle(index)}
                      disabled={singleOrder.items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <Label className="text-base">Delivery Address</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Street Address"
                    value={singleOrder.deliveryAddress.street}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        deliveryAddress: {
                          ...singleOrder.deliveryAddress,
                          street: e.target.value,
                        },
                      })
                    }
                  />
                  <Input
                    placeholder="City"
                    value={singleOrder.deliveryAddress.city}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        deliveryAddress: {
                          ...singleOrder.deliveryAddress,
                          city: e.target.value,
                        },
                      })
                    }
                  />
                  <Input
                    placeholder="State"
                    value={singleOrder.deliveryAddress.state}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        deliveryAddress: {
                          ...singleOrder.deliveryAddress,
                          state: e.target.value,
                        },
                      })
                    }
                  />
                  <Input
                    placeholder="Zip Code"
                    value={singleOrder.deliveryAddress.zipCode}
                    onChange={(e) =>
                      setSingleOrder({
                        ...singleOrder,
                        deliveryAddress: {
                          ...singleOrder.deliveryAddress,
                          zipCode: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={singleOrder.notes}
                  onChange={(e) =>
                    setSingleOrder({ ...singleOrder, notes: e.target.value })
                  }
                  placeholder="Order notes (optional)"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleSingleSubmit}
                disabled={isCreating}
                className="w-full"
              >
                {isCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  "Create Single Order"
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* --- Bulk Manual Tab --- */}
        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Manual Entry</CardTitle>
              <CardDescription>
                Add multiple orders one by one then submit all at once.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {bulkOrders.map((order, orderIndex) => (
                <div
                  key={orderIndex}
                  className="p-4 border rounded-lg relative space-y-4 bg-muted/20"
                >
                  <div className="absolute right-2 top-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBulkOrderRow(orderIndex)}
                      disabled={bulkOrders.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <h3 className="font-semibold">Order #{orderIndex + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input
                      placeholder="Email"
                      value={order.userInfo.email}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].userInfo.email = e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                    <Input
                      placeholder="Name"
                      value={order.userInfo.name}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].userInfo.name = e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                    <Input
                      placeholder="Street"
                      value={order.deliveryAddress.street}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].deliveryAddress.street =
                          e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                    <Input
                      placeholder="City"
                      value={order.deliveryAddress.city}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].deliveryAddress.city =
                          e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                    <Input
                      placeholder="State"
                      value={order.deliveryAddress.state}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].deliveryAddress.state =
                          e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                    <Input
                      placeholder="Zip"
                      value={order.deliveryAddress.zipCode}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].deliveryAddress.zipCode =
                          e.target.value;
                        setBulkOrders(newOrders);
                      }}
                    />
                  </div>

                  {/* Simplified Product Entry for Bulk: Select one product & qty to keep UI clean, or improve later */}
                  <div className="flex gap-3">
                    <Select
                      value={order.items[0].productId}
                      onValueChange={(val) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].items[0].productId = val;
                        setBulkOrders(newOrders);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      className="w-24"
                      placeholder="Qty"
                      value={order.items[0].quantity}
                      onChange={(e) => {
                        const newOrders = [...bulkOrders];
                        newOrders[orderIndex].items[0].quantity =
                          parseInt(e.target.value) || 1;
                        setBulkOrders(newOrders);
                      }}
                    />
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addBulkOrderRow}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Another Order
              </Button>
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleBulkSubmit}
                disabled={isBulkCreating}
                className="w-full"
              >
                {isBulkCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  `Create ${bulkOrders.length} Orders`
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* --- CSV Import Tab --- */}
        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle>Import CSV</CardTitle>
              <CardDescription>
                Upload a CSV file containing order details.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg bg-muted/10">
                <Upload className="h-10 w-10 text-muted-foreground mb-4" />
                <Label htmlFor="csv-upload" className="cursor-pointer mb-2">
                  <span className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90">
                    Select CSV File
                  </span>
                  <Input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  />
                </Label>
                {csvFile && (
                  <p className="text-sm font-medium">{csvFile.name}</p>
                )}
                <p className="text-xs text-muted-foreground mt-4">
                  Accepted format: .csv
                </p>
              </div>

              <div className="bg-muted p-4 rounded-md">
                <h4 className="font-semibold mb-2">CSV Format Specification</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Ensure your CSV has the following headers:
                  <code className="bg-background px-1 rounded">
                    email, name, phone, address_label, street, city, state, zip,
                    items, paymentMethod, notes
                  </code>
                </p>
                <p className="text-sm text-muted-foreground">
                  Items column format:{" "}
                  <code className="bg-background px-1 rounded">
                    productId:quantity|productId:quantity
                  </code>
                </p>
                <Button
                  variant="link"
                  onClick={downloadCsvTemplate}
                  className="p-0 h-auto mt-2"
                >
                  <FileDown className="h-4 w-4 mr-2" /> Download Template
                </Button>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleCsvUpload}
                disabled={!csvFile || isImporting}
                className="w-full"
              >
                {isImporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  "Upload and Import"
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
